import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/compat/router';

import { space } from 'styled-system';
import SignUpForm from 'components/forms/sign-up-form';
import useUI from 'src/hooks/use-ui';
import useUser from 'src/hooks/use-user';
import { Divider } from 'src/components/divider';
import { AppleLogin, GoogleLogin } from 'src/components/social-login';
import { useRedirectUrlForMobileEcomm } from 'src/components/modals/login/redirect-mobile-ecomm';
import { isReferralsRoute as isReferralsRouteUtils } from 'src/components/loyalty/rewards/dpos-utils';

export type SignupContentProps = {
  onClose?: () => void;
  analyticsEventLabel?: string;
  redirectUrl?: string | null;
  isEmbedMode?: boolean;
  skipPostSignupSequence?: boolean;
};

export function SignupContent({
  onClose,
  analyticsEventLabel,
  redirectUrl = null,
  isEmbedMode = false,
  skipPostSignupSequence = false,
}: SignupContentProps): JSX.Element {
  const { token } = useUser();
  const router = useRouter();
  const query = router?.query ?? {};
  const UI = useUI();
  const { isMobileEcommApp } = UI;
  const mobileEcommRedirectUrl = useRedirectUrlForMobileEcomm({ query, token });
  const publicContextSessionId = Array.isArray(query['session-id'])
    ? query['session-id'][0]
    : query['session-id'] ?? null;

  useEffect(() => {
    if (!isEmbedMode && mobileEcommRedirectUrl) {
      window.location.href = mobileEcommRedirectUrl;
    }
  }, [isEmbedMode, mobileEcommRedirectUrl]);

  const isReferralsRoute = router ? isReferralsRouteUtils(router) : false;

  return (
    <ContentContainer pb={isMobileEcommApp ? 30 : 40}>
      {isReferralsRoute && (
        <SubHeaderText>Enjoy loyalty perks, saved preferences, and a smoother shopping experience.</SubHeaderText>
      )}
      <SocialLogins>
        <GoogleLogin
          redirectUrl={redirectUrl ?? window.location.href}
          publicContextSessionId={publicContextSessionId}
        />
        <AppleLogin redirectUrl={redirectUrl ?? window.location.href} publicContextSessionId={publicContextSessionId} />
      </SocialLogins>

      <Divider text='or manually enter your information' />

      <SignUpForm
        onClose={() => {
          // In embed mode this redirectUrl is the current signup page, not a real destination —
          // account creation alone isn't loyalty enrollment success, so skip navigating and let
          // the reactive enrollment flow (LoyaltyRewards) fire the real loyalty:success event.
          if (redirectUrl && !isEmbedMode) {
            window.location.href = redirectUrl;
          }

          onClose?.();
        }}
        analyticsEventLabel={analyticsEventLabel}
        publicContextSessionId={publicContextSessionId}
        skipPostSignupSequence={skipPostSignupSequence}
      />
    </ContentContainer>
  );
}

export const ContentContainer = styled.div`
  padding: 2px 40px;
  height: 100%;

  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    padding-right: 34px;
    padding-left: 34px;
  }

  ${space}
`;

export const SubHeaderText = styled.p`
  font-size: 14px;
  font-weight: 200;
  color: var(--dcm-modal-text-color, #454e50);
  text-align: left;
  margin: 10px 2px 24px 2px;
`;

const SocialLogins = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;
